import { rpx } from 'client/lib/rpx-client';
import { IcoLogo } from '@components/icons';
import { useCurrentTenant } from '@components/router/session-context';
import { useDocumentTitle } from 'client/utils/use-document-title';
import { useState } from 'preact/hooks';
import { showError } from '@components/app-error';
import { BtnPrimary, BtnSecondary } from '@components/buttons';
import { NotificationPreferences } from '../student-course-preferences/notification-preferences';
import { showToast } from '@components/toaster';
import { DigestEmailSubscription, StudentCoursePreferences } from 'server/types';
import { useIntl } from 'shared/intl/use-intl';
import { LoadedProps, RouteLoadProps } from '@components/router';
import { defStudentRoute } from '@course/components/courses-app-router';

export const route = defStudentRoute({
  isPublic: true,
  Page,
  load,
});

/*
 * This page modifies student notification preferences.
 * The backend needs the code query param to be able to understand
 * the user id and course id.
 */
function Page(props: LoadedProps<typeof load>) {
  const intl = useIntl();
  const { code } = props.params;
  const { course } = props.state;
  const [preferences, setPreferences] = useState(props.state.preferences);
  const [isSaving, setIsSaving] = useState(false);
  const tenant = useCurrentTenant();

  useDocumentTitle(['Unsubscribe', course.title]);

  async function save(newPreferences: StudentCoursePreferences) {
    if (isSaving) {
      return;
    }

    setIsSaving(true);

    try {
      await rpx.courses.saveStudentCoursePreferencesWithToken({
        token: code,
        preferences: newPreferences,
      });
      showToast({
        type: 'ok',
        title: intl('Saved'),
        message: intl('Your preferences have been saved.'),
      });
    } catch (err) {
      showError(err);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div class="py-10">
      <header class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-center sm:justify-start">
        <IcoLogo class="h-12 sm:h-16" url={tenant.iconUrl} />
      </header>
      <main class="max-w-7xl mx-auto p-8">
        <NotificationPreferences
          courseTitle={course.title}
          guideName={course.guideName}
          preferences={preferences}
          save={(newPreferences) => setPreferences(newPreferences)}
        />
        <footer class="flex flex-col lg:flex-row justify-end lg:space-x-8 space-y-4 lg:space-y-0">
          <BtnSecondary
            class="px-8 py-4 rounded-full"
            isLoading={isSaving}
            onClick={() => {
              const resettedPreferences = {
                ...preferences,
                subscribedToAnnouncements: false,
                subscribedToMeetingReminders: false,
                subscribedToCommentReplies: false,
                digestEmailSubscription: 'off' as DigestEmailSubscription,
              };

              setPreferences(resettedPreferences);
              save(resettedPreferences);
            }}
          >
            {intl('Unsubscribe from all')}
          </BtnSecondary>
          <BtnPrimary
            class="px-8 py-4 rounded-full"
            isLoading={isSaving}
            onClick={() => save(preferences)}
          >
            {intl('Save Changes')}
          </BtnPrimary>
        </footer>
      </main>
    </div>
  );
}

function load(route: RouteLoadProps) {
  return rpx.courses.getStudentCoursePreferencesWithToken({
    token: encodeURIComponent(route.params.code),
  });
}
